<template>
  <div class="container">
    <div v-for="(group, gIndex) in internalRules" :key="'group_' + gIndex"
         class="my-4 border border-dashed border-primary p-5 rounded position-relative">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span>Groupe {{ gIndex + 1 }}</span>
        <div @click="removeGroup(gIndex)" class="btn btn-xs btn-icon btn-danger position-absolute"
             style="right:-10px;top:-10px">
          <i class="fad fa-times"></i>
        </div>
      </div>

      <!-- Conditions du groupe -->
      <div v-for="(rule, rIndex) in group.conditions" :key="'rule_' + gIndex + '_' + rIndex"
           class="d-flex align-items-center mb-2">
        <!-- Propriété -->
        <div class="me-2">
          <select class="form-select form-control" v-model="rule.property">
            <option v-for="prop in item.properties" :key="prop.uuid" :value="prop.uuid">
              {{ prop.name }}
            </option>
          </select>
        </div>

        <!-- Opérateur -->
        <div class="mx-2">
          <select class="form-select form-control" v-model="rule.operator">
            <option v-for="op in operators" :key="op.symbol" :value="op.symbol">
              {{ op.name }}
            </option>
          </select>
        </div>

        <!-- Valeur -->
        <div class="me-2">
          <div v-if="getProperty(rule.property)">
            <template v-if="getProperty(rule.property).type.code === 'RADIO'">
              <select v-model="rule.value" class="form-select form-control">
                <option v-for="choice in getProperty(rule.property).choices" :key="choice.code" :value="choice.code">
                  {{ choice.label }}
                </option>
              </select>
            </template>

            <template v-else-if="getProperty(rule.property).type.code === 'NUMBER'">
              <input type="number" class="form-control" v-model="rule.value"/>
            </template>
            <template v-else>
              <input type="text" class="form-control" v-model="rule.value" placeholder="Valeur"/>
            </template>
          </div>
          <div v-else>
            <input type="text" class="form-control" disabled placeholder="Sélectionnez une propriété"/>
          </div>
        </div>

        <!-- Supprimer une condition -->
        <div @click="removeCondition(gIndex, rIndex)" class="btn btn-xs btn-danger btn-icon">
          <i class="fad fa-times"></i>
        </div>
      </div>

      <!-- Ajouter une condition -->
      <div class="d-flex justify-content-center pt-2">
        <div @click="addCondition(gIndex)" class="btn btn-sm btn-primary">Ajouter une condition</div>
      </div>

      <!-- Évaluateur textuel -->
      <div class="mt-2">
        <strong>Conditions du groupe:</strong>
        <div>{{ describeGroup(group.conditions) }}</div>
      </div>

      <!-- Résultat de ce groupe -->
      <div class="mt-2">
        <strong>Résultat:</strong>
        <select class="form-select form-control" v-model="group.result">
          <option v-for="result in results" :key="result" :value="result">
            {{ result }}
          </option>
        </select>
      </div>
    </div>

    <!-- Ajouter un groupe -->
    <div class="d-flex justify-content-center my-2">
      <div @click="addGroup" class="mt-3 btn btn-icon btn-primary btn-xs">
        <i class="fad fa-plus"></i>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch} from 'vue-property-decorator'

@Component
export default class WorkflowEditor extends Vue {
  @Prop({required: true}) item!: any
  @Prop({required: true}) value!: any

  internalRules: any[] = []
  results: string[] = ['yellow', 'blue', 'green']  // Résultats possibles

  operators = [
    {symbol: '==', name: 'égal à'},
    {symbol: '!=', name: 'différent de'},
    {symbol: '>', name: 'supérieur à'},
    {symbol: '<', name: 'inférieur à'}
  ]

  mounted() {
    // Vérifier si `value` est un objet et contient bien une propriété `rules`
    if (!Array.isArray(this.value)) {
      this.internalRules = [{result: '', conditions: [{property: '', operator: '', value: ''}]}]  // Valeur par défaut
    } else {
      // Copie profonde de `value.rules` pour initialiser `internalRules`
      this.internalRules = JSON.parse(JSON.stringify(this.value)).map((group: any) => ({
        ...group,
        result: group.result || '',  // Ajouter un champ result par défaut
      }))
    }
  }

  @Watch('internalRules', {deep: true})
  onRulesChanged() {
    this.$emit('input', this.internalRules)
  }

  getProperty(uuid: string) {
    return this.item.properties.find((p: any) => p.uuid === uuid)
  }

  describeGroup(group: any[]): string {
    const parts = group.map((rule) => {
      const prop = this.getProperty(rule.property)
      const propName = prop ? prop.name : '[?]'
      const op = this.operators.find(o => o.symbol === rule.operator)?.name || rule.operator
      return `${propName} ${op} "${rule.value}"`
    })
    return parts.join(' ET ')
  }

  addGroup() {
    this.internalRules.push({result: '', conditions: [{property: '', operator: '', value: ''}]})
  }

  addCondition(groupIndex: number) {
    this.internalRules[groupIndex].conditions.push({property: '', operator: '', value: ''})
  }

  removeGroup(groupIndex: number) {
    this.internalRules.splice(groupIndex, 1)
  }

  removeCondition(groupIndex: number, conditionIndex: number) {
    this.internalRules[groupIndex].conditions.splice(conditionIndex, 1)
  }
}
</script>

<style scoped>
select.form-select {
  min-width: 180px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
